/**
 * debugSessionApi — standalone API module for the Debug Session Builder
 * (services/api.ts is frozen; mirrors the smokeTestApi precedent). Screenshots
 * are sent as multipart Blobs, not base64, so a high-res true-pixel capture
 * never risks the request-body limit. Cookie auth (withCredentials) means the
 * screenshot <img> URLs and the ZIP download carry the admin session too.
 */
import axios from 'axios';

const api = axios.create({ baseURL: '/api', withCredentials: true });

export type FindingType = 'bug' | 'design' | 'feature' | 'observation';

export interface DebugFinding {
  seq: number;
  title: string | null;
  note: string | null;
  finding_type: FindingType;
  severity: string;
  route: string | null;
  source_ref: string | null;
  element: any;
  annotations: any;
  network: any;
  console: any;
  repro: any;
  backend: any;
  has_screenshot: boolean;
  has_annotated: boolean;
  shot_count: number;
  shot_type: string | null;
  linked_ticket_id: number | null;
  created_at: string;
}

export interface DebugSessionDetail {
  session_token: string;
  title: string | null;
  environment: string;
  status: string;
  kept: boolean;
  deploy_sha: string | null;
  repro: any;
  started_at: string;
  completed_at: string | null;
  findings: DebugFinding[];
}

export interface DebugSessionSummary {
  session_token: string;
  title: string | null;
  environment: string;
  status: string;
  kept: boolean;
  finding_count: number;
  started_at: string;
  completed_at: string | null;
}

export interface FindingPayload {
  title?: string;
  note?: string;
  finding_type?: FindingType;
  severity?: string;
  route?: string;
  source_ref?: string;
  element?: any;
  annotations?: any;
  network?: any;
  console?: any;
  repro?: any;
  shot_type?: string;
  /** Per-extra-shot metadata, aligned with the `extra` upload order. */
  extra_meta?: Array<{ shot_type?: string; label?: string }>;
}

export const debugSessionApi = {
  create: (input: { title?: string; environment?: string; deploy_sha?: string; repro?: any }): Promise<{ session_token: string }> =>
    api.post('/debug-sessions', input).then((r) => r.data.data),

  list: (status?: string): Promise<DebugSessionSummary[]> =>
    api.get('/debug-sessions', { params: status ? { status } : {} }).then((r) => r.data.data),

  get: (token: string): Promise<DebugSessionDetail> =>
    api.get(`/debug-sessions/${token}`).then((r) => r.data.data),

  addFinding: (
    token: string,
    payload: FindingPayload,
    shots?: { screenshot?: Blob | null; annotated?: Blob | null; extra?: Blob[] },
  ): Promise<DebugFinding> => {
    const fd = new FormData();
    fd.append('payload', JSON.stringify(payload));
    if (shots?.screenshot) fd.append('screenshot', shots.screenshot, 'finding.jpg');
    if (shots?.annotated) fd.append('annotated', shots.annotated, 'finding-annotated.jpg');
    (shots?.extra || []).forEach((b, i) => fd.append('extra', b, `finding-extra-${i + 1}.jpg`));
    return api.post(`/debug-sessions/${token}/findings`, fd).then((r) => r.data.data);
  },

  updateFinding: (token: string, seq: number, patch: Partial<FindingPayload> & { linked_ticket_id?: number | null; backend?: any }): Promise<DebugFinding> =>
    api.patch(`/debug-sessions/${token}/findings/${seq}`, patch).then((r) => r.data.data),

  deleteFinding: (token: string, seq: number): Promise<{ deleted: boolean }> =>
    api.delete(`/debug-sessions/${token}/findings/${seq}`).then((r) => r.data.data),

  backendSnapshot: (token: string, seq: number, body: { failed_requests?: any[]; note?: string }): Promise<DebugFinding> =>
    api.post(`/debug-sessions/${token}/findings/${seq}/backend-snapshot`, body).then((r) => r.data.data),

  finalize: (token: string): Promise<{ ok: boolean; finding_count: number }> =>
    api.post(`/debug-sessions/${token}/finalize`).then((r) => r.data.data),

  setKept: (token: string, kept: boolean): Promise<{ kept: boolean }> =>
    api.post(`/debug-sessions/${token}/keep`, { kept }).then((r) => r.data.data),

  remove: (token: string): Promise<{ deleted: boolean }> =>
    api.delete(`/debug-sessions/${token}`).then((r) => r.data.data),

  /** Same-origin, cookie-authed URL for an <img> in the read-only detail view.
   *  Pass a number to address an EXTRA shot by index (the evidence tray beyond
   *  the primary/annotated pair). */
  screenshotUrl: (token: string, seq: number, variant: 'primary' | 'annotated' | number = 'primary'): string => {
    const base = `/api/debug-sessions/${token}/findings/${seq}/screenshot`;
    if (typeof variant === 'number') return `${base}?shot=${variant}`;
    return `${base}${variant === 'annotated' ? '?variant=annotated' : ''}`;
  },

  /** Fetch the ZIP as a blob (so auth applies) and trigger a browser download. */
  downloadZip: async (token: string, filename?: string): Promise<void> => {
    const res = await api.get(`/debug-sessions/${token}/export.zip`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `debug-session-${token.slice(0, 8)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

export default debugSessionApi;
